import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import FeatherIcon from 'feather-icons-react';
import { DeleteDocuments } from '../../graphql/taxDocument';
import { useDispatch } from 'react-redux';
import { importAlertState } from 'src/redux/actions/alert';
import { setUpdateDocuments } from 'src/redux/actions/documentostributarios';
import CustomModal from '../configuracion/components/customModal';
import ModalActionsFooter from 'src/components/shared/ModalActionsFooter';
import { getDocumentsInNominaInfo, useNominaGuardModalBodyClass } from 'src/utils/documentNominaGuard';
import { getObligationTypeDisplayLabel } from 'src/components/general/js/forms/reconcile/components/matchRowHelpers';
import { formatMoneyWithCurrency, formatRut } from '../../utils/formatter';
import './ModalDeleteObligations.scss';

const MAX_LIST_PREVIEW = 4;

function ObligationDeleteSummary({ document }) {
  const { t } = useTranslation('documents');
  if (!document) return null;

  const meta = document.meta || {};
  const isReceived = meta.isReceived === true;
  const typeLabel = getObligationTypeDisplayLabel(meta);
  const amount = document.amount ?? meta.amount ?? 0;
  const currency = document.currencyType || meta.currency || 'CLP';
  const docNumber = meta.documentId ?? meta.number;
  const partyName = isReceived
    ? meta.receiverName || meta.issuerName
    : meta.issuerName || meta.receiverName;
  const partyRut = isReceived
    ? meta.receiverId || meta.issuerId
    : meta.issuerId || meta.receiverId;

  return (
    <div className="delete-obligation-modal-summary">
      <div className="delete-obligation-modal-summary-type">{typeLabel}</div>
      {docNumber != null && docNumber !== '' && (
        <div className="delete-obligation-modal-summary-row">
          <span className="delete-obligation-modal-summary-label">{t('deleteObligation.docNumber')}</span>
          <span className="delete-obligation-modal-summary-value">{String(docNumber)}</span>
        </div>
      )}
      {partyName && (
        <div className="delete-obligation-modal-summary-row">
          <span className="delete-obligation-modal-summary-label">{t('deleteObligation.counterparty')}</span>
          <span className="delete-obligation-modal-summary-value">
            {partyName}
            {partyRut ? ` · ${formatRut(partyRut)}` : ''}
          </span>
        </div>
      )}
      <div className="delete-obligation-modal-summary-row">
        <span className="delete-obligation-modal-summary-label">{t('deleteObligation.amount')}</span>
        <span className="delete-obligation-modal-summary-value">
          {formatMoneyWithCurrency(amount, currency)}
        </span>
      </div>
    </div>
  );
}

function ObligationDeleteContent({ selectedDocuments }) {
  const { t } = useTranslation('documents');
  const count = selectedDocuments?.length || 0;
  const isSingle = count === 1;

  const listPreview = useMemo(() => {
    if (isSingle || count === 0) return [];
    return (selectedDocuments || []).slice(0, MAX_LIST_PREVIEW).map((doc) => {
      const meta = doc?.meta || {};
      return {
        id: doc?._id || doc?.id,
        label: getObligationTypeDisplayLabel(meta),
        amount: doc?.amount ?? meta.amount,
        currency: doc?.currencyType || meta.currency || 'CLP',
      };
    });
  }, [selectedDocuments, isSingle, count]);

  const remainingCount = Math.max(0, count - MAX_LIST_PREVIEW);

  return (
    <div className="delete-obligation-modal-body">
      <div className="delete-obligation-modal-icon" aria-hidden>
        <FeatherIcon icon="trash-2" size={22} />
      </div>

      <p className="delete-obligation-modal-lead">
        {isSingle
          ? t('deleteObligation.leadSingle')
          : t('deleteObligation.leadPlural', { count })}
      </p>

      {isSingle ? (
        <ObligationDeleteSummary document={selectedDocuments[0]} />
      ) : (
        <ul className="delete-obligation-modal-list">
          {listPreview.map((item) => (
            <li key={item.id} className="delete-obligation-modal-list-item">
              {item.label}
              {' · '}
              {formatMoneyWithCurrency(item.amount, item.currency)}
            </li>
          ))}
        </ul>
      )}

      {remainingCount > 0 && (
        <p className="delete-obligation-modal-more">
          {t('deleteObligation.andMore', { count: remainingCount })}
        </p>
      )}

      <p className="delete-obligation-modal-warning">
        {t('deleteObligation.warningMatches', {
          target: isSingle
            ? t('deleteObligation.targetSingle')
            : t('deleteObligation.targetPlural'),
        })}
      </p>
    </div>
  );
}

function ModalDeleteObligations({
  trigger,
  open: controlledOpen,
  onOpenChange,
  selectedDocuments,
  setSelectedDocs,
  companyId,
  setSelectedAmountDocs,
  onDeleted,
}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = (value) => {
    if (isControlled) {
      onOpenChange?.(value);
    } else {
      setInternalOpen(value);
    }
  };
  const [showNominaGuardModal, setShowNominaGuardModal] = useState(false);
  const [nominaGuardNames, setNominaGuardNames] = useState([]);
  const dispatch = useDispatch();
  const { t } = useTranslation('documents');

  useNominaGuardModalBodyClass(showNominaGuardModal);

  const { deleteDocuments, fetching } = DeleteDocuments();

  const count = selectedDocuments?.length || 0;
  const isSingle = count === 1;

  const onCancel = () => {
    if (!fetching) setOpen(false);
  };

  const onConfirm = async () => {
    try {
      const { canDelete, nominaNames } = getDocumentsInNominaInfo(selectedDocuments || []);
      if (!canDelete && nominaNames.length > 0) {
        setOpen(false);
        setNominaGuardNames(nominaNames);
        setShowNominaGuardModal(true);
        return;
      }
      const ids = (selectedDocuments || []).map((doc) => doc?._id || doc?.id).filter(Boolean);

      if (!ids.length) {
        dispatch(
          importAlertState({
            content: t('deleteObligation.selectAtLeastOne'),
            state: 'warning',
            duration: 4,
          }),
        );
        return;
      }

      dispatch(
        importAlertState({
          content: isSingle ? t('deleteObligation.deletingOne') : t('deleteObligation.deletingMany'),
          state: 'loading',
          duration: 10,
        }),
      );

      const result = await deleteDocuments({ ids, companyId });

      if (result?.data?.deleteDocuments) {
        dispatch(
          importAlertState({
            content: isSingle
              ? t('deleteObligation.deletedOne')
              : t('deleteObligation.deletedMany'),
            state: 'success',
            duration: 4,
          }),
        );
        setSelectedDocs?.([]);
        if (setSelectedAmountDocs) {
          setSelectedAmountDocs([]);
        }
        dispatch(setUpdateDocuments({ value: true }));
        onDeleted?.();
      } else {
        // El backend dice por qué no se pudo (nómina, período cerrado, conciliación): mostrarlo tal cual.
        const msg =
          result?.error?.graphQLErrors?.[0]?.message || result?.error?.message || t('deleteObligation.deleteError');
        dispatch(
          importAlertState({
            content: msg,
            state: 'error',
            duration: 8,
          }),
        );
      }
    } catch (e) {
      dispatch(
        importAlertState({
          content: e?.message || t('deleteObligation.deleteError'),
          state: 'error',
          duration: 4,
        }),
      );
    } finally {
      setOpen(false);
    }
  };

  return (
    <>
      <CustomModal
        onClose={onCancel}
        {...(trigger ? { onOpen: () => setOpen(true), trigger } : {})}
        open={open}
        size="small"
        dimmer="blurring"
        closeOnDimmerClick={!fetching}
        closeOnEscape={!fetching}
        classNameModal="delete-obligation-modal"
        classNameContent="delete-obligation-modal-content"
        title={isSingle ? t('deleteObligation.title') : t('deleteObligation.titlePlural')}
        content={<ObligationDeleteContent selectedDocuments={selectedDocuments} />}
        cancelarLabel={t('common.cancel')}
        confirmLabel={t('deleteObligation.confirmDelete')}
        onConfirm={onConfirm}
        confirmVariant="danger"
        isLoading={fetching}
      />

      <CustomModal
        open={showNominaGuardModal}
        onClose={() => setShowNominaGuardModal(false)}
        size="small"
        dimmer="blurring"
        classNameModal="nomina-guard-modal"
        title={t('deleteObligation.cannotDeleteTitle')}
        content={
          <div className="nomina-guard-modal-body">
            <p>
              {nominaGuardNames.length === 1
                ? t('deleteObligation.nominaGuardSingle')
                : t('deleteObligation.nominaGuardMany')}
            </p>
            <ul>
              {nominaGuardNames.map((name, i) => (
                <li key={i}>{name}</li>
              ))}
            </ul>
            <p className="nomina-guard-modal-hint">
              {t('deleteObligation.nominaGuardHint', {
                target:
                  nominaGuardNames.length === 1
                    ? t('deleteObligation.nominaTargetSingle')
                    : t('deleteObligation.nominaTargetPlural'),
              })}
            </p>
          </div>
        }
        hideActions={false}
        customActions={
          <ModalActionsFooter
            confirmLabel={t('common.understood')}
            onConfirm={() => setShowNominaGuardModal(false)}
            confirmVariant="primary"
          />
        }
      />
    </>
  );
}

export default ModalDeleteObligations;
